import { useCallback, useMemo } from "react";
import type { AxisSpec } from "../ChartSpec";
import { useDataViewerStore } from "../data-viewer-provider";
import { getLeftAxes } from "../spec/axes";

export interface LeftYAxisSelection {
    /** Left-side axes, in spec order — the candidates the metric picker switches between. */
    leftAxes: AxisSpec[];
    /** The axis whose scale is currently shown on the left (undefined if there are none). */
    activeLeftAxisId: string | undefined;
    setActiveLeftAxisId: (axisId: string) => void;
}

/**
 * Owns left y-axis selection for panels with multiple left axes.
 * Left-side axes are mutually exclusive on screen; this picks the visible one and
 * keeps the choice valid as the spec's axes change underneath it.
 */
export function useLeftYAxisSelection(panelId: string, axes: AxisSpec[]): LeftYAxisSelection {
    const leftAxes = useMemo(() => getLeftAxes(axes), [axes]);

    const preferredLeftAxisId = useDataViewerStore((state) => state.leftAxisSelections[panelId]);
    const setLeftAxisSelection = useDataViewerStore((state) => state.setLeftAxisSelection);

    const activeLeftAxisId = useMemo(() => {
        if (preferredLeftAxisId && leftAxes.some((a) => a.id === preferredLeftAxisId)) {
            return preferredLeftAxisId;
        }
        return leftAxes[0]?.id;
    }, [leftAxes, preferredLeftAxisId]);

    const setActiveLeftAxisId = useCallback(
        (axisId: string) => setLeftAxisSelection(panelId, axisId),
        [panelId, setLeftAxisSelection],
    );

    return { leftAxes, activeLeftAxisId, setActiveLeftAxisId };
}
